import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    Button,
    FormControl,
    FormLabel,
    Select,
    RadioGroup,
    Radio,
    Stack,
    Flex,
    Checkbox
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { useTranslation } from "react-i18next";
import InlineErrorInput from "../InlineErrorInput/InlineErrorInput";

const DiscountCodeModal = ({ isOpen, onClose, onSave, initialData, ticketTypes }) => {
    const { t } = useTranslation("default");

    const [discountCode, setDiscountCode] = useState({
        id: null,
        code: '',
        type: 'Percentage',
        value: '',
        scope: 'WholeOrder',
        eventTicketTypeId: '',
        isActive: true,
        hasUsageLimit: false,
        maxUsageCount: '',
        hasValidityPeriod: false,
        validFrom: '',
        validUntil: '',
    });

    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});

    useEffect(() => {
        if (!isOpen) return;
        const d = initialData ?? {};
        setDiscountCode({
            id: d.id ?? null,
            code: d.code ?? '',
            type: d.type ?? 'Percentage',
            value: d.value?.toString() ?? '',
            scope: d.scope ?? 'WholeOrder',
            eventTicketTypeId: d.eventTicketTypeId ?? '',
            isActive: d.isActive ?? true,
            hasUsageLimit: d.maxUsageCount != null,
            maxUsageCount: d.maxUsageCount?.toString() ?? '',
            hasValidityPeriod: !!(d.validFrom || d.validUntil),
            validFrom: d.validFrom ? dayjs(d.validFrom).format('YYYY-MM-DD') : '',
            validUntil: d.validUntil ? dayjs(d.validUntil).format('YYYY-MM-DD') : '',
        });
        setErrors({});
        setTouched({});
    }, [isOpen, initialData]);

    const validate = (state) => {
        const e = {};
        const value = Number(state.value);

        if (!state.code.trim()) e.code = t('validation.required');

        if (state.value === '' || Number.isNaN(value)) e.value = t('discountCode.validation.value_required');
        else if (value <= 0) e.value = t('discountCode.validation.value_positive');
        else if (state.type === 'Percentage' && value > 100) e.value = t('discountCode.validation.value_percentage_max');

        if (state.scope === 'SpecificTicketType' && !state.eventTicketTypeId)
            e.eventTicketTypeId = t('discountCode.validation.ticket_type_required');

        if (state.hasUsageLimit) {
            const maxUsage = Number(state.maxUsageCount);
            if (state.maxUsageCount === '' || Number.isNaN(maxUsage)) e.maxUsageCount = t('discountCode.validation.max_usage_required');
            else if (!Number.isInteger(maxUsage) || maxUsage <= 0) e.maxUsageCount = t('discountCode.validation.max_usage_positive');
        }

        if (state.hasValidityPeriod && state.validFrom && state.validUntil && dayjs(state.validUntil).isBefore(dayjs(state.validFrom)))
            e.validUntil = t('discountCode.validation.valid_until_after_from');

        return e;
    };

    const handleChange = (field, value) => {
        const next = { ...discountCode, [field]: value };
        setDiscountCode(next);
        setErrors(validate(next));
    };

    const handleBlur = (field) => {
        setTouched(prev => ({ ...prev, [field]: true }));
    };

    const handleSubmit = () => {
        const e = validate(discountCode);
        setErrors(e);
        if (Object.keys(e).length > 0) {
            setTouched({ code: true, value: true, eventTicketTypeId: true, maxUsageCount: true, validUntil: true });
            return;
        }

        onSave({
            id: discountCode.id,
            code: discountCode.code.trim().toUpperCase(),
            type: discountCode.type,
            value: parseFloat(discountCode.value),
            scope: discountCode.scope,
            eventTicketTypeId: discountCode.scope === 'SpecificTicketType' ? discountCode.eventTicketTypeId : null,
            isActive: discountCode.isActive,
            maxUsageCount: discountCode.hasUsageLimit ? parseInt(discountCode.maxUsageCount) : null,
            validFrom: discountCode.hasValidityPeriod && discountCode.validFrom
                ? dayjs(discountCode.validFrom).startOf('day').toISOString() : null,
            validUntil: discountCode.hasValidityPeriod && discountCode.validUntil
                ? dayjs(discountCode.validUntil).endOf('day').toISOString() : null,
        });
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="xl">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader bgColor={'#F8F5F4'}>{t('discountCode.header')}</ModalHeader>
                <ModalCloseButton />
                <ModalBody bgColor={'#F8F5F4'}>
                    <Flex flexDirection="column" bgColor="white" p="16px" borderRadius={'12px'} gap="8px">
                        <InlineErrorInput label={t('discountCode.form.code')} value={discountCode.code}
                            placeholder={t('discountCode.form.placeholder.code')} useFormik={false}
                            onChange={e => handleChange('code', e.target.value.toUpperCase().replace(/\s+/g, ''))}
                            onBlur={() => handleBlur('code')}
                            error={errors.code} touched={touched.code} isRequired
                        />

                        <FormControl mb={2}>
                            <FormLabel>{t('discountCode.form.type')}</FormLabel>
                            <RadioGroup value={discountCode.type} onChange={val => handleChange('type', val)}>
                                <Stack direction="row">
                                    <Radio value="Percentage">{t('discountCode.form.type_percentage')}</Radio>
                                    <Radio value="Amount">{t('discountCode.form.type_amount')}</Radio>
                                </Stack>
                            </RadioGroup>
                        </FormControl>

                        <InlineErrorInput type="number" step="0.01" inputMode="decimal"
                            label={discountCode.type === 'Percentage' ? t('discountCode.form.value_percentage') : t('discountCode.form.value_amount')}
                            value={discountCode.value} useFormik={false}
                            onChange={e => handleChange('value', e.target.value)}
                            onBlur={() => handleBlur('value')}
                            error={errors.value} touched={touched.value} isRequired
                        />

                        <FormControl mb={2}>
                            <FormLabel>{t('discountCode.form.scope')}</FormLabel>
                            <Select value={discountCode.scope} onChange={e => handleChange('scope', e.target.value)}>
                                <option value="WholeOrder">{t('discountCode.form.scope_whole_order')}</option>
                                <option value="SpecificTicketType">{t('discountCode.form.scope_specific_ticket')}</option>
                            </Select>
                        </FormControl>

                        {discountCode.scope === 'SpecificTicketType' && (
                            <FormControl mb={2} isInvalid={touched.eventTicketTypeId && !!errors.eventTicketTypeId} isRequired>
                                <FormLabel>{t('discountCode.form.ticket_type')}</FormLabel>
                                <Select placeholder={t('discountCode.form.select_ticket_type')} value={discountCode.eventTicketTypeId}
                                    onChange={e => handleChange('eventTicketTypeId', e.target.value)}
                                    onBlur={() => handleBlur('eventTicketTypeId')}
                                >
                                    {(ticketTypes || []).filter(tt => tt.id).map(tt => (
                                        <option key={tt.id} value={tt.id}>{tt.name}</option>
                                    ))}
                                </Select>
                            </FormControl>
                        )}

                        <FormControl>
                            <Checkbox isChecked={discountCode.hasUsageLimit} onChange={e => handleChange('hasUsageLimit', e.target.checked)}>
                                {t('discountCode.form.has_usage_limit')}
                            </Checkbox>
                        </FormControl>
                        {discountCode.hasUsageLimit && (
                            <InlineErrorInput type="number" inputMode="numeric" label={t('discountCode.form.max_usage_count')}
                                value={discountCode.maxUsageCount} useFormik={false}
                                onChange={e => handleChange('maxUsageCount', e.target.value)}
                                onBlur={() => handleBlur('maxUsageCount')}
                                error={errors.maxUsageCount} touched={touched.maxUsageCount} isRequired
                            />
                        )}

                        <FormControl>
                            <Checkbox isChecked={discountCode.hasValidityPeriod} onChange={e => handleChange('hasValidityPeriod', e.target.checked)}>
                                {t('discountCode.form.has_validity_period')}
                            </Checkbox>
                        </FormControl>
                        {discountCode.hasValidityPeriod && (
                            <Flex gap={4} alignItems="flex-start">
                                <InlineErrorInput type="date" flex={1} label={t('discountCode.form.valid_from')}
                                    value={discountCode.validFrom} useFormik={false}
                                    onChange={e => handleChange('validFrom', e.target.value)}
                                    onBlur={() => handleBlur('validFrom')}
                                />
                                <InlineErrorInput type="date" flex={1} label={t('discountCode.form.valid_until')}
                                    value={discountCode.validUntil} useFormik={false}
                                    onChange={e => handleChange('validUntil', e.target.value)}
                                    onBlur={() => handleBlur('validUntil')}
                                    error={errors.validUntil} touched={touched.validUntil}
                                />
                            </Flex>
                        )}

                        <FormControl>
                            <Checkbox isChecked={discountCode.isActive} onChange={e => handleChange('isActive', e.target.checked)}>
                                {t('discountCode.form.is_active')}
                            </Checkbox>
                        </FormControl>
                    </Flex>
                </ModalBody>

                <ModalFooter bgColor={'#F8F5F4'}>
                    <Button bg="transparent" color="brand.500" px="48px" py="25px" onClick={onClose}>{t('cancel')}</Button>
                    <Button bg="brand.500" color="white" borderRadius={'12px'} _hover={{ bg: 'brand.600' }} px="48px" py="25px"
                        isDisabled={Object.keys(errors).length > 0} onClick={handleSubmit}>{t('discountCode.save')}</Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default DiscountCodeModal;
